import { useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { IonButton, IonFab, IonFabButton, IonIcon, IonList, IonItem, IonLabel, IonModal, IonText, IonAlert } from '@ionic/react';
import { scanOutline } from 'ionicons/icons';
import { isAxiosError } from 'axios';

import { getLoad, refundLoad } from '@/actions/loads';

import { inDollars, formatDate, titleize, loadNumber } from '@/helpers/humanize';
import { currentLocker } from '@/helpers/loads';
import { scanQRCode } from '@/helpers/qr_code';
import useResourceLoader from '@/hooks/useResourceLoader';
import Page from '@/pages/Page';
import NotFoundPage from '@/pages/NotFoundPage';
import NextStateButton from '@/components/NextStateButton';
import PrintLoadModal from '@/components/PrintLoadModal';
import LockerOpenModal from '@/components/LockerOpenModal';
import PriceDescription from '@/components/loads/PriceDescription';

const CompanyLoadPage = () => {
  const history = useHistory();
  const {uuid} = useParams();
  const dispatch = useDispatch();
  const loading = useResourceLoader(getLoad(uuid));
  const user = useSelector((state) => state.user);
  const load = useSelector((state) => _.find(state.loads, {uuid}));
  const location = useSelector((state) => load ? _.find(state.locations, {uuid: load.location_id}) : undefined);
  const modal = useRef(null);

  if (!loading && !load) return <NotFoundPage type="Load"/>;

  const showCancel = ["manager", "owner"].includes(user.role) && load?.state != "canceled";
  const showRefund = ["manager", "owner"].includes(user.role) && load?.charged_cents > 0;
  const showReweigh = load?.payment_status !== 'paid' && _.includes(['weighed', 'accepted', 'rejected'], load?.state);

  const priceAfterDiscount = load?.price_cents - (load?.coupon?.discount_cents ?? 0);
  const lockerNumber = currentLocker(load, location)?.number;

  return load && <>
    <Page title={`Load ${loadNumber(load.number)} for ${load.consumer_name || load.consumer_email}`}
      backDefault={`/loads/${load.state}`}
      primaryButton={<IonButton fill="solid" id="open-modal">Print</IonButton>}
      loading={loading}
    >
      <IonFab slot="fixed" vertical="bottom" horizontal="end">
        <IonFabButton onClick={scanQRCode(history, 'loads')}>
          <IonIcon icon={scanOutline}></IonIcon>
        </IonFabButton>
      </IonFab>
      <IonList>
        <IonItem>
          <IonLabel>
            <h4>Pickup/Dropoff Location</h4>
            {load.location_name}
            <p>{location?.address}</p>
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel>
            <h4>Customer Dropped This Off At</h4>
            {formatDate(load.created_at)}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel>
            <h4>Current State</h4>
            {titleize(load.state)}{load.status_note ? ` (${load.status_note})` : ''}
            {lockerNumber && <IonText color="medium"> (Locker {lockerNumber})</IonText>}
            <p>Last updated {formatDate(load.updated_at)}</p>
          </IonLabel>
          <NextStateButton load={load} user={user} location={location}/>
        </IonItem>
        {load.washing_instructions && (
          <IonItem>
            <IonLabel>
              <h4>Washing Instructions</h4>
              {load.washing_instructions}
            </IonLabel>
          </IonItem>
        )}
        <IonItem>
          <IonLabel>
            <h4>Weight</h4>
            {_.isNil(load.weight_us_lbs) ? "unweighed" : `${_.round(load.weight_us_lbs, 2)} lbs`}
          </IonLabel>
          {showReweigh && (
            <IonButton routerLink={`/loads/${load.uuid}/weigh`} color="secondary">
              Change Weight & Price
            </IonButton>
          )}
        </IonItem>
        <IonItem>
          <IonLabel>
            <h4>Price</h4>
            {!_.isNil(load.weight_us_lbs) ? <>
              <span style={load.coupon?.discount_cents && {textDecoration: 'line-through'}}>
                {load.price_cents ? inDollars(load.price_cents) : "Free"}
              </span>
              {" "}
              <small>{load.cost_cents ? ` (${inDollars(load.cost_cents)}/lbs)` : ""}</small>
              {load.coupon?.discount_cents > 0 && (
                <div>
                  {priceAfterDiscount ? inDollars(priceAfterDiscount) : "Free"}
                  {" "}
                  <small><IonText color="success">
                    -{inDollars(load.coupon.discount_cents)} with coupon code <code style={{fontWeight: 'bold'}}>{load.coupon.code}</code>
                  </IonText></small>
                </div>
              )}
              <PriceDescription load={load}/>
            </> : "N/A"}
            {load.tip_cents > 0 && (
              <p><IonText color="success">Tipped <strong>{inDollars(load.tip_cents)}</strong></IonText></p>
            )}
            {load.payment_status != 'unpaid' && load.charged_cents < load.price_cents && (
              <p><IonText color="warning">Refunded {inDollars(load.price_cents - load.charged_cents)}</IonText></p>
            )}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel>
            <h4>Payment</h4>
            {titleize(load.payment_status)}
            {load.payment_message ? ` (${load.payment_message})` : ""}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel>
            <h4>Customer</h4>
            {load.consumer_name}
            <p><a href={`tel:${load.consumer_phone_number}`}>{load.consumer_phone_number}</a></p>
          </IonLabel>
        </IonItem>
        {showCancel && (
          <IonItem routerLink={`/loads/${load.uuid}/cancel`}>
            <IonText color="warning">Cancel Load</IonText>
          </IonItem>
        )}
        {showRefund && (
          <IonItem id="issue-refund">
            <IonText color="warning">Issue Refund</IonText>
          </IonItem>
        )}
      </IonList>
      <IonModal ref={modal} trigger="open-modal">
        <PrintLoadModal load={load} location={location} dismiss={() => modal.current?.dismiss()}/>
      </IonModal>
      <IonAlert trigger="issue-refund"
        header="Refund Amount"
        buttons={[
          'Cancel',
          {
            text: 'Issue Refund',
            role: 'confirm',
            handler: async ({refundAmount, reason}) => {
              try {
                await dispatch(refundLoad({uuid: load.uuid, cents: _.round(refundAmount*100), reason}));
              } catch (e) {
                if (isAxiosError(e)) {
                  console.warn(e);
                  alert(`Could not issue refund: ${_.get(e, 'response.data.error', e.message)}.`);
                } else {
                  throw(e);
                }
              }
            },
          }]}
        inputs={[
          {
            type: 'number',
            name: 'refundAmount',
            min: 0,
            max: load.charged_cents/100,
            value: load.charged_cents/100
          },
          {
            placeholder: 'reason',
            name: 'reason'
          }
        ]}/>
    </Page>
    <LockerOpenModal user={user} load={load} location={location}/>
  </>;
};

export default CompanyLoadPage;
